/*
********************************************************************************
PageFlowManager — behavior layer over the page-flow-mapping.js data.

Remembers the current vendor/direction context so callers don't repeat it, and
answers navigation questions ("what's next / previous?", "is this the last
page?") and the submit-completeness question ("what pages does this flow
require?").

Instantiated once in web-components.js as `app.flowManager`, after vendor and
direction are known. Vendor/direction are set once from navsData at bootstrap
and never change mid-session, so there is no updateContext() — the flow is
resolved once in the constructor.

See docs/page-flow-refactoring-plan.md and memory project_page_flow_refactor.
********************************************************************************
*/

import { PAGES, PAGE_FLOWS } from "../shared/page-flow-mapping.js";

export class PageFlowManager {
	constructor(vendor, direction) {
		// Normalize inputs so callers can pass either app.state.direction
		// ("Delivery"/"Return", titleCased) or navsData.direction
		// ("delivery"/"return", raw) — both resolve to the same flow key.
		this.vendor = vendor === "Mercedes-Benz" ? "Mercedes-Benz" : "default";
		this.direction = String(direction).toLowerCase();
		this.key = `${this.vendor}-${this.direction}`;
		this.currentFlow = this.#loadFlow();
	}

	// Resolve the flow's page keys into full entries, stamping the id (the map
	// key) back on. The id lives once in the data — here it is re-attached so
	// consumers can read entry.pageId.
	#loadFlow() {
		let ids = PAGE_FLOWS[this.key];

		if (!ids) {
			console.warn(`PageFlowManager: no flow for "${this.key}", falling back to "default-return"`);
			ids = PAGE_FLOWS["default-return"];
		}

		return ids.map((id) => ({ pageId: id, ...PAGES[id] }));
	}

	// The ordered array of resolved page entries for the current flow.
	getFlow() {
		return this.currentFlow;
	}

	// Look up a page entry by its route (e.g. "/tire-inspection").
	getByRoute(route) {
		return this.currentFlow.find((p) => p.route === route);
	}

	// Look up a page entry by pageId (e.g. "page8").
	getPageById(pageId) {
		return this.currentFlow.find((p) => p.pageId === pageId);
	}

	// The entry after `currentRoute`, or undefined if last / not in flow.
	getNext(currentRoute) {
		const i = this.currentFlow.findIndex((p) => p.route === currentRoute);
		if (i === -1) return undefined;
		return this.currentFlow[i + 1];
	}

	// The entry before `currentRoute`, or undefined if first / not in flow.
	getPrevious(currentRoute) {
		const i = this.currentFlow.findIndex((p) => p.route === currentRoute);
		if (i === -1) return undefined;
		return this.currentFlow[i - 1];
	}

	isFirstPage(route) {
		return this.currentFlow[0]?.route === route;
	}

	isLastPage(route) {
		return this.currentFlow[this.currentFlow.length - 1]?.route === route;
	}

	// Build the menu items for the current flow (Phase 4). Markup matches the
	// existing menu templates: an <ion-router-link> wrapping an <ion-item>.
	// The static "Application Settings" item is prepended by the caller.
	generateMenuHTML() {
		return this.currentFlow
			.map(
				(page) => `
			<ion-menu-toggle auto-hide="false">
				<ion-router-link href="#${page.route}">
					<ion-item>
						<ion-label color="primary">${page.label}</ion-label>
					</ion-item>
				</ion-router-link>
			</ion-menu-toggle>`
			)
			.join("");
	}
}
